'use client';

import type { FlagCatalogEntry } from '@/@feature-flags/catalog';
import { resolvedFlagDescription, visibleFlagCatalog } from '@/@feature-flags/catalog';
import { isFeatureFlagsQaDashboardEnabled } from '@/@feature-flags/config';
import { useFeatureFlagAdmin } from '@/@feature-flags/hooks/useFeatureFlagAdmin';
import type { FeatureFlag } from '@/@feature-flags/types/entities';
import { formatDateTime } from '@/lib/utils/formatting';
import {
    Button,
    Chip,
    Pagination,
    Tab,
    Table,
    Tabs,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Tooltip,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { StaffPage } from '@/components/staff-tools/staff-page';
import {
    CellStack,
    RowActions,
    RowButton,
    STAFF_TABLE,
    StaffToolbar,
} from '@/components/staff-tools/staff-table';
import { useCallback, useRef, useState } from 'react';
import { AddOverrideModal } from './AddOverrideModal';
import { CreateFlagModal } from './CreateFlagModal';
import { EditFlagModal } from './EditFlagModal';
import { FlagDetailPanel } from './FlagDetailPanel';
import { KnownFlagsPanel } from './KnownFlagsPanel';
import { FLAG_ADMIN_SCROLL_ATTR, useAlignSectionOnChange } from './scroll-section';

export function FeatureFlagsAdmin() {
    const {
        flags,
        total,
        page,
        limit,
        setPage,
        isLoadingList,
        listError,
        createdKeys,
        isLoadingKeys,
        isKeysError,
        selectedFlag,
        setSelectedFlag,
        flagDetail,
        isLoadingDetail,
        createFlag,
        isCreating,
        updateFlag,
        isUpdating,
        deleteFlag,
        isDeleting,
        addOverride,
        isAddingOverride,
        removeOverride,
        isRemovingOverride,
    } = useFeatureFlagAdmin();

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [createSeed, setCreateSeed] = useState<FlagCatalogEntry | null>(null);
    const knownFlags = visibleFlagCatalog(isFeatureFlagsQaDashboardEnabled());
    const createSuggestions = knownFlags.filter((entry) => !createdKeys?.has(entry.key));
    const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
    const [overrideTarget, setOverrideTarget] = useState<string | null>(null);
    const [tab, setTab] = useState('saved');

    const totalPages = Math.max(1, Math.ceil(total / limit));
    const environmentRef = useRef<HTMLElement>(null);
    useAlignSectionOnChange(environmentRef, page);

    const handleDelete = useCallback(
        async (flag: FeatureFlag) => {
            if (!window.confirm(`Delete feature flag "${flag.key}" and all its overrides?`)) return;
            await deleteFlag(flag.key);
        },
        [deleteFlag],
    );

    const handleRemoveOverride = useCallback(
        (overrideId: number) => {
            if (!selectedFlag) return;
            removeOverride(selectedFlag.key, overrideId);
        },
        [selectedFlag, removeOverride],
    );

    return (
        <StaffPage
            width="wide"
            actions={
                <Button
                    color="primary"
                    size="sm"
                    onPress={() => {
                        setCreateSeed(null);
                        setIsCreateOpen(true);
                    }}
                    startContent={<Icon icon="lucide:plus" className="h-4 w-4" aria-hidden />}
                >
                    New Flag
                </Button>
            }
        >
            <StaffToolbar
                filters={
                    <Tabs
                        aria-label="Feature flag lists"
                        variant="underlined"
                        color="primary"
                        selectedKey={tab}
                        onSelectionChange={(key) => setTab(String(key))}
                        classNames={{ tab: 'w-auto px-1', tabList: 'p-0', panel: 'hidden' }}
                    >
                        <Tab key="saved" title="Saved" />
                        <Tab key="catalog" title="Catalog" />
                    </Tabs>
                }
                loading={tab === 'saved' && isLoadingList}
                count={
                    tab === 'saved' && !isLoadingList
                        ? `${total.toLocaleString()} flag${total === 1 ? '' : 's'}`
                        : null
                }
            />

            <div className="flex items-start gap-4">
                <div {...{ [FLAG_ADMIN_SCROLL_ATTR]: '' }} className="flex min-w-0 flex-1 flex-col">
                    <div hidden={tab !== 'catalog'}>
                        <KnownFlagsPanel
                            entries={knownFlags}
                            createdKeys={createdKeys}
                            isLoadingKeys={isLoadingKeys}
                            isKeysError={isKeysError}
                            onCreate={(entry) => {
                                setCreateSeed(entry);
                                setIsCreateOpen(true);
                            }}
                        />
                    </div>

                    <section
                        ref={environmentRef}
                        aria-label="Saved"
                        hidden={tab !== 'saved'}
                        className="flex flex-col gap-3"
                    >
                        <p className="text-small text-foreground-600">
                            Flags saved in this environment. A saved flag can still be off.
                        </p>
                        {listError ? (
                            <p role="alert" className="text-small text-danger">
                                Failed to load feature flags
                            </p>
                        ) : (
                            <>
                                <Table
                                    aria-label="Feature Flags"
                                    classNames={STAFF_TABLE}
                                    selectionMode="single"
                                    selectedKeys={
                                        selectedFlag
                                            ? new Set([String(selectedFlag.id)])
                                            : new Set()
                                    }
                                    onSelectionChange={(keys) => {
                                        if (keys === 'all') return;
                                        const id = Array.from(keys)[0];
                                        const flag = flags.find((f) => String(f.id) === String(id));
                                        setSelectedFlag(flag ?? null);
                                    }}
                                >
                                    <TableHeader>
                                        <TableColumn>Flag</TableColumn>
                                        <TableColumn>Internal</TableColumn>
                                        <TableColumn>External</TableColumn>
                                        <TableColumn>Overrides</TableColumn>
                                        <TableColumn>Updated</TableColumn>
                                        <TableColumn width={120} align="end">
                                            <span className="sr-only">Actions</span>
                                        </TableColumn>
                                    </TableHeader>
                                    <TableBody
                                        emptyContent={
                                            isLoadingList ? ' ' : 'No feature flags found'
                                        }
                                    >
                                        {(isLoadingList ? [] : flags).map((flag) => (
                                            <TableRow key={String(flag.id)}>
                                                <TableCell className="max-w-[24rem]">
                                                    <div className="flex flex-col gap-1">
                                                        <CellStack
                                                            primary={
                                                                <span className="font-mono">
                                                                    {flag.key}
                                                                </span>
                                                            }
                                                            secondary={
                                                                <FlagDescriptionCell flag={flag} />
                                                            }
                                                        />
                                                        {flag.releaseEnv === null && (
                                                            <Tooltip
                                                                content="This flag has no environment set and affects all environments sharing this database."
                                                                delay={300}
                                                            >
                                                                <Chip
                                                                    size="sm"
                                                                    variant="flat"
                                                                    color="warning"
                                                                    className="w-fit cursor-default"
                                                                >
                                                                    global
                                                                </Chip>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="sm"
                                                        variant="flat"
                                                        color={
                                                            flag.enabledInternal
                                                                ? 'success'
                                                                : 'default'
                                                        }
                                                    >
                                                        {flag.enabledInternal ? 'On' : 'Off'}
                                                    </Chip>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="sm"
                                                        variant="flat"
                                                        color={
                                                            flag.enabledExternal
                                                                ? 'success'
                                                                : 'default'
                                                        }
                                                    >
                                                        {flag.enabledExternal ? 'On' : 'Off'}
                                                    </Chip>
                                                </TableCell>
                                                <TableCell className="tabular-nums">
                                                    {flag.overrides?.length ?? 0}
                                                </TableCell>
                                                <TableCell className="whitespace-nowrap">
                                                    {formatDateTime(flag.updatedAt)}
                                                </TableCell>
                                                <TableCell>
                                                    <RowActions
                                                        label={`flag ${flag.key}`}
                                                        primary={
                                                            <RowButton
                                                                icon="lucide:pencil"
                                                                onPress={() => setEditingFlag(flag)}
                                                                aria-label="Edit flag"
                                                            >
                                                                Edit
                                                            </RowButton>
                                                        }
                                                        menu={[
                                                            {
                                                                key: 'delete',
                                                                label: 'Delete flag',
                                                                icon: 'lucide:trash-2',
                                                                danger: true,
                                                                disabled: isDeleting,
                                                                onPress: () =>
                                                                    void handleDelete(flag),
                                                            },
                                                        ]}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                {totalPages > 1 && (
                                    <div className="flex justify-center">
                                        <Pagination
                                            size="sm"
                                            total={totalPages}
                                            page={page}
                                            onChange={setPage}
                                            showControls
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                </div>

                {/* Detail Panel */}
                {tab === 'saved' && selectedFlag && (
                    <div className="w-96 shrink-0 rounded-large bg-content1 p-4 shadow-small">
                        <FlagDetailPanel
                            flag={flagDetail}
                            isLoading={isLoadingDetail}
                            onAddOverride={() => setOverrideTarget(selectedFlag.key)}
                            onRemoveOverride={handleRemoveOverride}
                            isRemovingOverride={isRemovingOverride}
                            onClose={() => setSelectedFlag(null)}
                        />
                    </div>
                )}
            </div>

            {/* Modals */}
            <CreateFlagModal
                isOpen={isCreateOpen}
                onClose={() => {
                    setIsCreateOpen(false);
                    setCreateSeed(null);
                }}
                onSubmit={createFlag}
                isSubmitting={isCreating}
                suggestions={createSuggestions}
                initialKey={createSeed?.key ?? ''}
                initialDescription={createSeed?.description ?? ''}
            />

            <EditFlagModal
                isOpen={!!editingFlag}
                onClose={() => setEditingFlag(null)}
                flag={editingFlag}
                onSubmit={updateFlag}
                isSubmitting={isUpdating}
            />

            {overrideTarget && (
                <AddOverrideModal
                    isOpen={!!overrideTarget}
                    onClose={() => setOverrideTarget(null)}
                    flagKey={overrideTarget}
                    onSubmit={addOverride}
                    isSubmitting={isAddingOverride}
                />
            )}
        </StaffPage>
    );
}

function FlagDescriptionCell({ flag }: { flag: FeatureFlag }) {
    const resolved = resolvedFlagDescription(flag.key, flag.description);
    return <span className="line-clamp-1">{resolved.text || '—'}</span>;
}
